import * as Sentry from '@sentry/react';

import { Box, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import ApiContext from 'contexts/apiContext';
import ErrorContext from 'contexts/errorContext';
import Paper from '@mui/material/Paper';
import Spinner from 'common/Spinner';
import StoreContext from 'contexts/storeContext';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { getDateFromString, getDateString } from 'utils';
import { GetApp } from '@mui/icons-material';
import { Receipt } from 'types/receipt';
import { createAndDownloadPdf } from 'pdf';

export default function ReceiptsTable() {
  const { api } = useContext(ApiContext);
  const { store, formatNumber } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);

  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const fetchReceipts = useCallback(
    (showLoader = true, pageNumber = 1, rows = rowsPerPage) => {
      setLoading(showLoader);
      api.receipts
        .list(store.id, { page: pageNumber, page_size: rows })
        .then((res) => {
          setReceipts(res.data.results);
          setPageCount(res.data.count);
        })
        .catch(setError)
        .finally(() => setLoading(false));
    },
    [api, rowsPerPage, setError, store.id],
  );

  const handleChangePage = (event: any, page: number) => {
    setCurrentPage(page + 1);
    fetchReceipts(true, page + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const rows = parseInt(event.target.value, 10);
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchReceipts(true, currentPage);
  }, [currentPage, fetchReceipts]);

  const downloadReceipt = (receipt: Receipt) => {
    setDownloadingIds((prev) => new Set(prev).add(receipt.id));
    api.receipts
      .getPdf(store.id, receipt.id)
      .then((res) => {
        createAndDownloadPdf(document, res.data, `recibo_${receipt.id}`);
      })
      .catch((error) => {
        setError(error);
        console.error(error);
        Sentry.captureException(error);
      })
      .finally(() => {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(receipt.id);
          return next;
        });
      });
  };

  return loading ? (
    <Box marginX={'auto'}>
      <Spinner />
    </Box>
  ) : receipts.length > 0 ? (
    <TableContainer component={Paper}>
      <Table aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Referencia</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {receipts.map((receipt) => (
            <TableRow key={receipt.id}>
              <TableCell>{getDateString(getDateFromString(receipt.date))}</TableCell>
              <TableCell>{formatNumber(receipt.amount)}</TableCell>
              <TableCell>{receipt.reference}</TableCell>
              <TableCell>
                {downloadingIds.has(receipt.id) ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    onClick={() => downloadReceipt(receipt)}
                    aria-label='Descargar recibo'
                    color='primary'
                    size='small'
                    sx={{ p: 0 }}
                  >
                    <GetApp fontSize='small' />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 20]}
        component='div'
        count={pageCount}
        rowsPerPage={rowsPerPage}
        page={currentPage - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage='Filas por página:'
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
      />
    </TableContainer>
  ) : (
    <Grid container justifyContent='center'>
      <Typography>Aún no tienes recibos.</Typography>
    </Grid>
  );
}
